/**
 * Event Theme Service — Centralized event identity system.
 *
 * Single source of truth for all event-type visual identity across:
 *   - Hero Carousel cards (CustomerDashboard)
 *   - Events List cards (CustomerDashboard)
 *   - Event Detail page header + summary card
 *   - Celebration overlay
 *   - Plan Event confirmation screen
 *
 * Accepts an event_type string, returns a complete theme object.
 * Do not duplicate this logic anywhere else.
 */

// ─── Mood Classification ───
// Maps event types to 4 emotional categories.
export const getMood = (eventType) => {
  const t = (eventType || '').toLowerCase();
  if (/wedding|engagement|bridal|gala|formal|ceremony|reception|vow/.test(t)) return 'elegant';
  if (/birthday|baby.?shower|graduation|anniversary|party|quincea|bar.?mitzvah|bat.?mitzvah|retirement|farewell|celebration|holiday|house.?warming|festive|new.?year|thanksgiving|hanukkah|diwali|eid|lunar/.test(t)) return 'celebration';
  if (/corporate|conference|meeting|summit|seminar|workshop|retreat|business|networking/.test(t)) return 'corporate';
  return 'default';
};

// ─── Decoration Variant ───
// Maps event types to 8 specific visual variants for SVG decorations.
export const getDecoVariant = (eventType) => {
  const t = (eventType || '').toLowerCase();
  if (/wedding|bridal|ceremony|reception|vow|gala|formal/.test(t)) return 'wedding';
  if (/engagement/.test(t)) return 'engagement';
  if (/anniversary/.test(t)) return 'anniversary';
  if (/baby.?shower/.test(t)) return 'babyshower';
  if (/graduation/.test(t)) return 'graduation';
  if (/house.?warming/.test(t)) return 'housewarming';
  if (/holiday|new.?year|thanksgiving|hanukkah|diwali|eid|lunar|festive/.test(t)) return 'holiday';
  if (/birthday|party|quincea|bar.?mitzvah|bat.?mitzvah|retirement|farewell|celebration/.test(t)) return 'birthday';
  return null;
};

// ─── Decoration Gradients ───
// Full-card background gradients for each decoration variant.
export const DECO_GRADIENTS = {
  birthday: 'linear-gradient(to right, #FEFCF9 0%, #FAF6EF 25%, #F5EEE0 55%, #EDE3CE 80%, #E8DBBF 100%)',
  wedding: 'linear-gradient(to right, #FFFEFB 0%, #FBF5EA 18%, #F5ECDA 40%, #EFE3CA 60%, #E8D9C0 78%, #E5D5B8 100%)',
  anniversary: 'linear-gradient(to right, #FFFCFA 0%, #FAF2EC 18%, #F2E6DC 40%, #EBDACB 62%, #E5D2BF 80%, #DFC9B2 100%)',
  babyshower: 'linear-gradient(to right, #FEFDFB 0%, #FAF6EF 18%, #F2EDE2 40%, #E8E1D0 60%, #DDD8C4 78%, #D6D0BC 100%)',
  graduation: 'linear-gradient(to right, #FDFCFE 0%, #F5F2FA 18%, #ECE7F4 40%, #E2DBED 62%, #D8D0E6 80%, #D2C9E0 100%)',
  engagement: 'linear-gradient(to right, #FFFCFA 0%, #FBF3ED 18%, #F4E8DA 40%, #EDDCC8 62%, #E6D2B8 80%, #E0CAAE 100%)',
  housewarming: 'linear-gradient(to right, #FEFCF9 0%, #FAF5ED 18%, #F2ECE0 40%, #EAE2D4 62%, #E2DACB 80%, #DCD3C2 100%)',
  holiday: 'linear-gradient(to right, #FEFEFE 0%, #F2EEF5 12%, #DDD5E8 28%, #B8ACD0 45%, #8878A8 60%, #5E4E80 75%, #3A2D5C 88%, #2A2048 100%)',
};

// ─── Mood Config ───
// Gradient, border, and countdown bg per mood category.
export const MOOD_CONFIG = {
  elegant: {
    gradient: 'linear-gradient(135deg, transparent 28%, rgba(255,255,255,0.10) 43%, rgba(255,255,255,0.20) 50%, rgba(255,255,255,0.10) 57%, transparent 72%), radial-gradient(ellipse at 82% 22%, rgba(220,200,160,0.22) 0%, transparent 45%), radial-gradient(ellipse at 15% 80%, rgba(198,169,107,0.08) 0%, transparent 40%), radial-gradient(circle at 68% 50%, rgba(248,240,225,0.45) 0%, transparent 32%), linear-gradient(150deg, #E3D6C0 0%, #EDE4D3 20%, #F6F0E5 48%, #FBF8F3 78%, #FEFDFB 100%)',
    countdownBg: 'bg-[#E8E1D4]/90 backdrop-blur-sm',
    cardBorder: 'border-[#D8D0C0]',
    accentColor: '#C6A96B',
    accentBg: 'bg-[#F8F4EC]',
  },
  celebration: {
    gradient: 'linear-gradient(to right, #FEFCF9 0%, #FAF6EF 30%, #F5EEE0 60%, #EDE3CE 85%, #E8DBBF 100%)',
    countdownBg: 'bg-[#EBE2D5]/90 backdrop-blur-sm',
    cardBorder: 'border-[#DCD4C5]',
    accentColor: '#C6A96B',
    accentBg: 'bg-[#FBF8F2]',
  },
  corporate: {
    gradient: 'linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.08) 45%, rgba(255,255,255,0.14) 50%, rgba(255,255,255,0.08) 55%, transparent 70%), radial-gradient(ellipse at 78% 25%, rgba(180,180,175,0.12) 0%, transparent 45%), linear-gradient(150deg, #E6E6E2 0%, #EEEEEB 30%, #F6F6F4 55%, #FBFBFA 85%, #FDFDFB 100%)',
    countdownBg: 'bg-[#E5E5E2]/90 backdrop-blur-sm',
    cardBorder: 'border-[#DCDCD8]',
    accentColor: '#8A8A82',
    accentBg: 'bg-[#F6F6F4]',
  },
  default: {
    gradient: 'linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.08) 44%, rgba(255,255,255,0.14) 50%, rgba(255,255,255,0.08) 56%, transparent 70%), radial-gradient(ellipse at 75% 25%, rgba(198,169,107,0.08) 0%, transparent 50%), linear-gradient(150deg, #E7E5DF 0%, #EFEDE8 30%, #F8F7F4 55%, #FCFBFA 85%, #FEFDFB 100%)',
    countdownBg: 'bg-[#E6E5E0]/90 backdrop-blur-sm',
    cardBorder: 'border-[#E0DED8]',
    accentColor: '#9A9A94',
    accentBg: 'bg-[#F8F7F4]',
  },
};

// ─── Badge Tint Map ───
// Variant-specific badge color overrides for the hero carousel.
const BADGE_TINTS = {
  wedding:      { booked: ['#C6A96B', '#9A8550'], active: ['#C6A96B', '#9A8550'] },
  anniversary:  { booked: ['#C4A898', '#A08070'], active: ['#C4A898', '#A08070'] },
  engagement:   { booked: ['#D4A894', '#B08878'], active: ['#D4A894', '#B08878'] },
  babyshower:   { booked: ['#B8C8AE', '#8AA880'], active: ['#B8C8AE', '#8AA880'] },
  graduation:   { booked: ['#B0A8C8', '#7868A0'], active: ['#9888B8', '#7868A0'] },
  holiday:      { booked: ['#B8ACD0', '#7868A0'], active: ['#A898C0', '#7868A0'] },
};

/**
 * Apply variant-specific badge tinting to a base badge style string.
 * Used by the hero carousel for event-aware badge colors.
 */
export const tintBadgeStyle = (baseStyle, decoVariant) => {
  const tint = BADGE_TINTS[decoVariant];
  if (!tint) return baseStyle;
  return baseStyle
    .replace(/#8FAF9F/g, tint.booked[0])
    .replace(/#5A8A6F/g, tint.booked[1])
    .replace(/#C6A96B/g, tint.active[0]);
};

// ─── Decoration Helpers ───
// Pre-computed flags for each variant (used by SVG decoration rendering).
export const getDecoFlags = (decoVariant) => {
  const dIsW = decoVariant === 'wedding';
  const dIsA = decoVariant === 'anniversary';
  return {
    dIsW,
    dIsA,
    dIsE: decoVariant === 'engagement',
    dIsBs: decoVariant === 'babyshower',
    dIsGr: decoVariant === 'graduation',
    dIsHw: decoVariant === 'housewarming',
    dIsHol: decoVariant === 'holiday',
    dIsClassic: decoVariant === 'birthday' || dIsW || dIsA,
    dGoldOp: dIsA ? 0.80 : dIsW ? 0.85 : 0.88,
    dGreenOp: decoVariant === 'birthday' ? 0.50 : 0,
    dGreen2Op: decoVariant === 'birthday' ? 0.32 : 0,
    dConfetti: dIsA ? 0.48 : dIsW ? 0.62 : 1,
  };
};

// ─── Unified Theme Accessor ───
/**
 * Get the complete visual theme for an event type.
 *
 * Usage:
 *   const theme = getEventTheme('Wedding');
 *   theme.mood          // 'elegant'
 *   theme.decoVariant   // 'wedding'
 *   theme.gradient      // background CSS
 *   theme.cardBorder    // Tailwind class
 *   theme.accentColor   // hex color for borders/accents
 *   theme.accentBg      // Tailwind class for subtle bg
 *   theme.countdownBg   // Tailwind class
 */
export const getEventTheme = (eventType) => {
  const mood = getMood(eventType);
  const decoVariant = getDecoVariant(eventType);
  const moodStyle = MOOD_CONFIG[mood];
  const gradient = decoVariant ? DECO_GRADIENTS[decoVariant] : moodStyle.gradient;

  return {
    mood,
    decoVariant,
    gradient,
    cardBorder: moodStyle.cardBorder,
    countdownBg: moodStyle.countdownBg,
    accentColor: moodStyle.accentColor,
    accentBg: moodStyle.accentBg,
    decoFlags: decoVariant ? getDecoFlags(decoVariant) : null,
  };
};

// ─── Event-Aware Copy ───
const EVENT_COPY = {
  birthday:     { headline: 'Let\u2019s get this party started', sub: 'Your celebration is in motion' },
  wedding:      { headline: 'Your wedding planning has officially begun', sub: 'Let\u2019s create something unforgettable' },
  anniversary:  { headline: 'Something special is on the way', sub: 'Celebrate your story beautifully' },
  babyshower:   { headline: 'A beautiful celebration is taking shape', sub: 'Everything is coming together' },
  graduation:   { headline: 'Time to celebrate this milestone', sub: 'You\u2019ve earned this moment' },
  engagement:   { headline: 'The next chapter starts here', sub: 'Let\u2019s make it extraordinary' },
  housewarming: { headline: 'Welcome home — let\u2019s celebrate', sub: 'Your new beginning deserves a great party' },
  holiday:      { headline: 'The festivities are taking shape', sub: 'Let\u2019s make this season memorable' },
};

const MOOD_COPY = {
  elegant:      { headline: 'Your event is officially underway', sub: 'Let\u2019s make it unforgettable' },
  celebration:  { headline: 'Planning has officially begun', sub: 'Your celebration is in motion' },
  corporate:    { headline: 'Your event is underway', sub: 'Let\u2019s make this seamless and successful' },
  default:      { headline: 'Planning has officially begun', sub: 'We\u2019re excited to bring this together' },
};

/**
 * Get event-type-aware copy for confirmation screens.
 *
 * Resolution: decoVariant copy → mood copy → default.
 *
 * Usage:
 *   const copy = getEventCopy('Wedding');
 *   copy.headline  // "Your wedding planning has officially begun"
 *   copy.sub       // "Let's create something unforgettable"
 */
export const getEventCopy = (eventType) => {
  const variant = getDecoVariant(eventType);
  if (variant && EVENT_COPY[variant]) return EVENT_COPY[variant];
  const mood = getMood(eventType);
  return MOOD_COPY[mood] || MOOD_COPY.default;
};

